/**
 * /wireline-slickline/invoices/ready ("Ready to invoice") — customer-approved
 * field tickets on no live invoice (GET /wireline-slickline/invoices/billable,
 * fieldiq-api#1359). Modelled on Pipeline & Facility's Ready to invoice.
 *
 * ONE PANEL PER GROUP, in the API's order. A group is the set one invoice may
 * bill: one customer, one province or state, one currency. Each group offers
 * "Create invoice", which opens New invoice with that group's tickets ticked —
 * where the office sets the due date and notes and generates. The API groups
 * the tickets and sums each group's subtotal; nothing here does either.
 *
 * The invoices themselves list in FINANCE → Invoices, the one invoice list for
 * every vertical. This page is the vertical's own way in, so it has no back
 * arrow. Supervisors and dispatchers read it; only admin and office staff are
 * offered "Create invoice" (the API refuses everyone else).
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWlBillable } from '../../../hooks/useWlInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { wlScopeText } from '../../../lib/wireline-slickline-tickets';
import { canWriteWlInvoices, wlBillableGroupKey, wlNewInvoiceForGroupPath } from '../../../lib/wireline-slickline-invoices';
import type { WlBillableGroup } from '../../../types/wireline-slickline-invoices';
import { useOpenRecord } from '../../downhole/recordPage';
import { WL_EYEBROW, WL_PRIMARY, WlEmpty, WlLoading, WlPanel } from '../WlBits';

const groupTitle = (g: WlBillableGroup) => g.customerName || '—';
const groupSub = (g: WlBillableGroup) => `${g.provinceState || 'Province or state not set'} · ${g.currency || '—'}`;

function ReadyGroup({ group, canWrite }: { group: WlBillableGroup; canWrite: boolean }) {
  const openRecord = useOpenRecord();
  return (
    <WlPanel
      eyebrow={groupSub(group)}
      title={groupTitle(group)}
      testId="wl-ready-group"
      actions={
        canWrite ? (
          <Link to={wlNewInvoiceForGroupPath(group)} className={WL_PRIMARY} data-testid="wl-ready-create">
            <Receipt className="size-4" aria-hidden="true" />
            Create invoice
          </Link>
        ) : undefined
      }
      footer={`${group.tickets.length} ticket${group.tickets.length === 1 ? '' : 's'} · ${fmtCurrency(group.subtotal, group.currency)} before tax`}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr className="border-l-4 border-l-transparent">
              <th scope="col" className="px-4 py-3">Ticket</th>
              <th scope="col" className="px-4 py-3">Job</th>
              <th scope="col" className="px-4 py-3">Covers</th>
              <th scope="col" className="px-4 py-3">Approved by</th>
              <th scope="col" className="px-4 py-3">PO</th>
              <th scope="col" className="px-4 py-3 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {group.tickets.map((t) => (
              <tr key={t.ticketId} className="border-t border-l-4 border-fieldiq-border border-l-fi-green" data-testid="wl-ready-ticket">
                <td className="px-4 py-2.5">
                  <button
                    type="button"
                    className="font-mono text-sm font-extrabold text-white underline-offset-2 hover:underline"
                    onClick={() => openRecord(`/wireline-slickline/tickets/${t.ticketId}`)}
                  >
                    {t.ticketNumber || '—'}
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <Link to={`/wireline-slickline/jobs/${t.jobId}?tab=tickets`} className="font-mono text-sm text-fieldiq-text underline-offset-2 hover:underline">
                    {t.jobNumber || '—'}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">{wlScopeText({ scope: t.scope, workDate: t.workDate })}</td>
                <td className="px-4 py-2.5 text-sm text-fieldiq-text">{t.customerApprovedByName || '—'}</td>
                <td className="px-4 py-2.5 font-mono text-sm text-fieldiq-text">{t.poNumber || '—'}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-sm tabular-nums text-white" data-testid="wl-ready-ticket-subtotal">{fmtCurrency(t.subtotal, group.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WlPanel>
  );
}

export default function WlReadyToInvoiceScreen() {
  const { user } = useAuth();
  const canWrite = canWriteWlInvoices(user?.role);
  const billable = useWlBillable();
  const failure = queryLoadFailure(billable);
  const groups = useMemo(() => billable.data?.data ?? [], [billable.data]);
  // A tally of the loaded rows (the API returns no count), never money.
  const ticketCount = groups.reduce((n, g) => n + g.tickets.length, 0);

  return (
    <div data-testid="wl-ready-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Ready to invoice"
        stats={[
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Tickets', sub: 'Approved, unbilled', tone: 'accent', loading: billable.isLoading, value: displayValue(billable.isLoading || failure ? undefined : ticketCount) },
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Groups', sub: 'One invoice each', tone: 'neutral', loading: billable.isLoading, value: displayValue(billable.isLoading || failure ? undefined : groups.length) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            Field tickets the customer has approved and no invoice bills yet, grouped the way one invoice may bill them: one customer, one province or state, one currency. Invoices list in{' '}
            <Link className="underline" to="/finance/invoices">Finance → Invoices</Link>.
          </p>
          {!canWrite && groups.length > 0 && <p className="text-sm text-fieldiq-muted" data-testid="wl-ready-read-only">Only an admin or office staff can create an invoice.</p>}
          {billable.isLoading ? (
            <WlLoading label="Loading tickets ready to invoice" />
          ) : failure ? (
            <WlPanel eyebrow="Approved field tickets" title="Ready to invoice">
              <LoadFailureNotice error={queryFailureError(billable)} kind={failure} subject="tickets ready to invoice" />
            </WlPanel>
          ) : groups.length === 0 ? (
            <WlPanel eyebrow="Approved field tickets" title="Ready to invoice" testId="wl-ready-empty">
              <WlEmpty>No customer-approved field tickets are waiting to be invoiced.</WlEmpty>
            </WlPanel>
          ) : (
            groups.map((g) => <ReadyGroup key={wlBillableGroupKey(g)} group={g} canWrite={canWrite} />)
          )}
        </div>
      </section>
    </div>
  );
}
